import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mountain, Flame, Award, Plus, LogOut, Snowflake } from "lucide-react";

const StudentDashboard = () => {
  const [me, setMe] = useState(null);
  const [expeditions, setExpeditions] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ topic: "", difficulty: "standard" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [meR, expR] = await Promise.all([api.get("/student/me"), api.get("/student/expeditions")]);
      setMe(meR.data);
      setExpeditions(expR.data.expeditions || []);
    } catch (err) {
      navigate("/student/login", { replace: true });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const logout = async () => {
    try { await api.post("/student/logout"); } catch {}
    navigate("/student/login", { replace: true });
  };

  const createExp = async (e) => {
    e.preventDefault();
    if (!form.topic.trim()) { toast.error("What do you want to learn?"); return; }
    setBusy(true);
    try {
      const r = await api.post("/student/expeditions/generate", form);
      toast.success("Your trail is ready — let's climb!");
      setShowNew(false);
      setForm({ topic: "", difficulty: "standard" });
      navigate(`/student/expeditions/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not build trail");
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <div className="min-h-screen bg-mm-parchment"><div className="text-center py-20 text-mm-navy/60">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      {/* Simplified navbar — students only see logout, no billing/settings */}
      <header className="sticky top-0 z-20 w-full bg-mm-parchment/95 backdrop-blur border-b border-mm-navy/10">
        <div className="container mx-auto flex items-center justify-between py-3 max-w-4xl">
          <Logo size={40} />
          <button onClick={logout} className="btn-trail btn-outline-navy text-sm" data-testid="student-logout-btn">
            <LogOut size={14} strokeWidth={2.5} /> Sign out
          </button>
        </div>
      </header>

      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail HQ</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy mt-2" data-testid="student-dash-title">Welcome back, {me.username}!</h1>

        {me.class_frozen && (
          <div className="mt-4 rounded-xl border-2 border-sky-300 bg-sky-50 p-4 flex items-center gap-3" data-testid="student-frozen-banner">
            <div className="w-10 h-10 rounded-full bg-sky-500 text-white flex items-center justify-center shrink-0">
              <Snowflake size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-sky-900">Your teacher paused the class</div>
              <p className="text-sm text-sky-900/80 mt-0.5">Hang tight — the trail will reopen as soon as your teacher unfreezes it.</p>
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-3 gap-3">
          <Stat icon={<Mountain size={16} />} label="Altitude Points" value={me.altitude_points} accent />
          <Stat icon={<Flame size={16} />} label="Compass Streak" value={`${me.compass_streak}d`} />
          <Stat icon={<Award size={16} />} label="Badges" value={(me.badges || []).length} />
        </div>

        <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading text-2xl font-black text-mm-navy">My expeditions</h2>
          <button onClick={() => setShowNew(true)} disabled={me.class_frozen} className="btn-trail btn-gold text-sm disabled:opacity-50" data-testid="student-new-expedition-btn">
            <Plus size={14} strokeWidth={2.5} /> Start a new trail
          </button>
        </div>

        <div className="mt-4 space-y-3" data-testid="student-expeditions-list">
          {expeditions.length === 0 && (
            <div className="text-center py-12 border-2 border-dashed border-mm-navy/15 rounded-xl text-mm-navy/60">
              No expeditions yet. Pick a topic and start climbing!
            </div>
          )}
          {expeditions.map((e) => (
            <Link
              key={e.id}
              to={`/student/expeditions/${e.id}`}
              className="stage-card p-5 flex items-center justify-between hover:border-mm-gold"
              data-testid={`student-exp-${e.id}`}
            >
              <div>
                <div className="font-heading text-lg font-black text-mm-navy">{e.topic}</div>
                <div className="text-xs text-mm-navy/60 mt-1">
                  {e.status === "completed" ? "Summited 🏔️" : `${(e.completed_stages || []).length}/5 stages`}
                </div>
              </div>
              <div className="text-mm-steel font-bold text-xs uppercase tracking-widest">Continue →</div>
            </Link>
          ))}
        </div>
      </div>

      {showNew && (
        <div className="fixed inset-0 z-50 bg-mm-navy/50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <form onSubmit={createExp} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl p-6 border-2 border-mm-navy/10 space-y-3"
            data-testid="student-new-expedition-form">
            <h3 className="font-heading text-2xl font-black text-mm-navy">What do you want to learn?</h3>
            <input
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              autoFocus
              placeholder="Sharks, fractions, the Roman Empire..."
              className="w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 text-lg"
              data-testid="student-new-topic-input"
            />
            <p className="text-xs text-mm-navy/70 leading-relaxed" data-testid="student-new-topic-hint">
              <b>Tip:</b> Specific topics work better than broad ones. Try <span className="font-mono text-mm-navy">Subatomic Particles</span> instead of <span className="font-mono text-mm-navy">The Atomic Theory</span>.
            </p>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowNew(false)} className="btn-trail btn-outline-navy text-sm flex-1">Cancel</button>
              <button type="submit" disabled={busy} className="btn-trail btn-gold text-sm flex-1 disabled:opacity-50" data-testid="student-new-expedition-submit">
                {busy ? "Building trail..." : "Build my trail!"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon, label, value, accent }) => (
  <div className={`p-3 rounded-lg text-center ${accent ? "bg-mm-navy text-white" : "bg-white border border-mm-navy/10"}`}>
    <div className={`flex items-center justify-center gap-1 text-[10px] uppercase tracking-widest font-bold ${accent ? "text-mm-gold" : "text-mm-steel"}`}>
      {icon}{label}
    </div>
    <div className={`font-heading text-xl font-black mt-1 ${accent ? "text-white" : "text-mm-navy"}`}>{value}</div>
  </div>
);

export default StudentDashboard;
